import { ComponentRef, useEffect, useRef, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";

import { AppHeader } from "@/blocks/AppHeader";
import { BackButton } from "@/blocks/BackButton";
import { OnboardingPagination } from "@/blocks/OnboardingPagination";
import { ONBOARDING_VARIANTS } from "@/blocks/OnboardingVariants/registry";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnScrollView } from "@/components/VnScrollView";
import { OnboardingProvider } from "@/context/OnboardingProvider";
import { useOnboardingVariantQuery } from "@/data/queries/onboardingQueries";
import { useGetShortCodeDataQuery } from "@/data/queries/verifyApiQueries";
import {
  DEFAULT_ONBOARDING_VARIANT_ID,
  resolveOnboardingVariantId,
} from "@/domain/onboarding";
import mixpanel from "@/services/mixpanel";
import {
  completedOnboardingMemberIdsAtom,
  onboardingShortCodeDataAtom,
} from "@/stores/atoms";
import { normalizeSearchParam } from "@/utils/routing";
import { cn } from "@/utils/tailwind";

const OnboardingScreen = () => {
  const { code, variant: variantOverride } = useLocalSearchParams<{
    code: string;
    variant?: string;
  }>();
  const shortCode = normalizeSearchParam(code);
  const { data: shortCodeData, isLoading: isLoadingShortCodeData } =
    useGetShortCodeDataQuery(shortCode);
  const setOnboardingShortCodeData = useSetAtom(onboardingShortCodeDataAtom);
  const completedMemberIds = useAtomValue(completedOnboardingMemberIdsAtom);

  const router = useRouter();

  const scrollViewRef = useRef<ComponentRef<typeof VnScrollView>>(null);
  const lastLoggedCodeRef = useRef<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  // Measured instead of read from `useWindowDimensions`, which on web reports
  // the device width rather than the laid-out page width when the page is
  // zoomed out (e.g. Android Chrome's "Desktop site" mode), leaving several
  // steps visible side by side.
  const [stepsSize, setStepsSize] = useState({ width: 0, height: 0 });

  // Skip onboarding for members who have already completed it on this device.
  const hasUserAlreadyOnboardedThisShortCode = Boolean(
    shortCodeData &&
    "memberId" in shortCodeData &&
    completedMemberIds.includes(shortCodeData.memberId),
  );

  // Lets devs preview any variant locally without touching Mixpanel.
  const devVariantOverride = __DEV__
    ? normalizeSearchParam(variantOverride)
    : "";

  // Asking Mixpanel enrolls the user in the experiment, so members being
  // redirected never ask.
  const { data: experimentVariantId, isLoading: isLoadingVariant } =
    useOnboardingVariantQuery({
      code: shortCode,
      entryPointType: shortCodeData?.entryPointType,
      workspaceId: shortCodeData?.workspaceId,
      isEnabled: !hasUserAlreadyOnboardedThisShortCode && !devVariantOverride,
    });

  const variantId = devVariantOverride
    ? resolveOnboardingVariantId(devVariantOverride)
    : (experimentVariantId ?? DEFAULT_ONBOARDING_VARIANT_ID);
  const { steps } = ONBOARDING_VARIANTS[variantId];

  // The route can receive a new short code without unmounting, and the code can
  // resolve to a variant with a different number of steps. Each code + variant
  // pair is its own flow: the step position is reset here, during render, so no
  // render uses a step from the previous flow, and the provider is keyed by it
  // so the concern picked in the previous flow is dropped too.
  const flowKey = `${shortCode}:${variantId}`;
  const [currentFlowKey, setCurrentFlowKey] = useState(flowKey);
  if (flowKey !== currentFlowKey) {
    setCurrentFlowKey(flowKey);
    setCurrentStep(1);
  }

  const isLoading =
    isLoadingShortCodeData ||
    hasUserAlreadyOnboardedThisShortCode ||
    isRedirecting ||
    isLoadingVariant;

  // Steps are hidden until they know their own size and how much space the
  // floating header and footer take, so their content never flashes underneath
  // them or renders at zero width.
  const isLayoutMeasured =
    headerHeight > 0 && footerHeight > 0 && stepsSize.width > 0;

  const logOnboardingDataToMixpanel = (data: Record<string, unknown>) => {
    mixpanel.track("Onboarding Short Code Data", {
      ...data,
    });
  };

  useEffect(() => {
    if (shortCodeData) {
      setOnboardingShortCodeData(shortCodeData);

      // Only log to Mixpanel once per short code, not on every effect re-run — and
      // re-log if the user lands on a different code without the route unmounting.
      if (lastLoggedCodeRef.current !== code) {
        lastLoggedCodeRef.current = code;

        // Omit `clinicLogoBase64` — it's a large base64 payload we don't need in analytics.
        const {
          clinicLogoBase64: _clinicLogoBase64,
          ...loggableShortCodeData
        } = shortCodeData;

        logOnboardingDataToMixpanel({
          ...loggableShortCodeData,
          hasUserAlreadyOnboardedThisShortCode,
        });
      }

      if (hasUserAlreadyOnboardedThisShortCode) {
        setIsRedirecting(true);
        router.replace("/(unauthed)/auth");
        return;
      }
    } else {
      setOnboardingShortCodeData(null);

      if (
        !isLoadingShortCodeData &&
        code &&
        lastLoggedCodeRef.current !== code
      ) {
        lastLoggedCodeRef.current = code;
        mixpanel.track("No data found for the given short code", {
          code,
        });
      }
    }
  }, [
    shortCodeData,
    isLoadingShortCodeData,
    hasUserAlreadyOnboardedThisShortCode,
    setOnboardingShortCodeData,
    router,
    code,
  ]);

  // Super properties ride along on every later event (e.g. `Screen Viewed` on
  // the auth screens), which is what lets Mixpanel compare sign-up conversion
  // per variant.
  useEffect(() => {
    if (!shortCodeData || isLoading) return;

    mixpanel.registerSuperProperties({
      onboarding_variant: variantId,
      onboarding_entry_point: shortCodeData.entryPointType,
    });
  }, [shortCodeData, isLoading, variantId]);

  useEffect(() => {
    if (isLoading) return;

    mixpanel.track("Onboarding Step Viewed", {
      onboarding_variant: variantId,
      step_index: currentStep,
      step_count: steps.length,
      entry_point_type: shortCodeData?.entryPointType,
    });
  }, [isLoading, currentStep, variantId, steps, shortCodeData?.entryPointType]);

  useEffect(() => {
    scrollViewRef.current?.scrollTo({
      x: (currentStep - 1) * stepsSize.width,
      animated: true,
    });
  }, [currentStep, stepsSize.width]);

  const isAtFirstStep = currentStep === 1;
  const isAtLastStep = currentStep === steps.length;

  const onNextStepPressed = () => {
    if (isAtLastStep) {
      mixpanel.track("Onboarding Completed", {
        onboarding_variant: variantId,
        entry_point_type: shortCodeData?.entryPointType,
      });
      router.navigate("/(unauthed)/auth");
    } else {
      setCurrentStep(currentStep => currentStep + 1);
    }
  };

  const onBackPress = () => {
    setCurrentStep(currentStep => Math.max(currentStep - 1, 1));
  };

  // Until the steps are visible, the header keeps its own maroon background
  // instead of relying on a step to paint it.
  const isShowingSteps = !isLoading && isLayoutMeasured;

  const footerTone = steps[currentStep - 1]?.footerTone ?? "light";
  const backButtonColorClassName =
    footerTone === "dark" ? "text-white" : "text-sidebar";

  const getPrimaryButtonTextI18nKey = () => {
    const stepButtonI18nKey = steps[currentStep - 1]?.primaryButtonI18nKey;

    if (stepButtonI18nKey) {
      return stepButtonI18nKey;
    }

    if (isAtFirstStep) {
      return "native:onboarding.buttons.getStarted";
    }

    if (isAtLastStep) {
      return "native:onboarding.buttons.createMyProfile";
    }

    return "common:continue";
  };

  return (
    <VnBox className="flex flex-1">
      {isLoading ? (
        <VnBox
          className="flex-1 items-center justify-center"
          style={{ paddingTop: headerHeight }}>
          <VnLoadingSpinner size={30} />
        </VnBox>
      ) : (
        <OnboardingProvider key={flowKey} variantId={variantId}>
          <VnBox
            className="flex w-full flex-1 flex-col overflow-hidden"
            style={{ opacity: isLayoutMeasured ? 1 : 0 }}
            onLayout={event => {
              const { width, height } = event.nativeEvent.layout;
              setStepsSize(previous =>
                previous.width === width && previous.height === height
                  ? previous
                  : { width, height },
              );
            }}>
            <VnScrollView
              horizontal
              className="flex flex-1 flex-row"
              scrollEnabled={false}
              ref={scrollViewRef}>
              {steps.map(({ Component }, index) => (
                // A variant's steps never reorder at runtime, so the index is a stable key.
                <Component
                  key={index}
                  width={stepsSize.width}
                  height={stepsSize.height}
                  headerHeight={headerHeight}
                  footerHeight={footerHeight}
                />
              ))}
            </VnScrollView>
          </VnBox>

          <VnBox
            className="pb-safe absolute bottom-0 left-0 right-0 items-center gap-5 px-4 py-4"
            onLayout={event =>
              setFooterHeight(event.nativeEvent.layout.height)
            }>
            <OnboardingPagination
              stepCount={steps.length}
              currentStep={currentStep}
              tone={footerTone}
            />
            <VnBox className="flex w-full flex-row justify-between md:max-w-screen-sm">
              {!isAtFirstStep && (
                <BackButton
                  onPress={onBackPress}
                  variant="ghost"
                  textClassName={backButtonColorClassName}
                  iconClassName={backButtonColorClassName}
                />
              )}
              <VnButton
                onPress={onNextStepPressed}
                i18nKey={getPrimaryButtonTextI18nKey()}
                className={cn("rounded-lg", {
                  "w-full": isAtFirstStep,
                  "w-[60%] md:w-[80%]": !isAtFirstStep,
                })}
              />
            </VnBox>
          </VnBox>
        </OnboardingProvider>
      )}

      {/* Rendered last so it floats above the steps, which paint the area under it. */}
      <VnBox
        className="absolute left-0 right-0 top-0"
        onLayout={event => setHeaderHeight(event.nativeEvent.layout.height)}>
        <AppHeader
          className={cn("px-4 py-4", {
            "bg-transparent": isShowingSteps,
          })}
          shouldShowLanguageSelector
          shouldShowThemeToggle={false}
        />
      </VnBox>
    </VnBox>
  );
};

export { OnboardingScreen };
